import { Component, DestroyRef, Inject, OnDestroy, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, filter, mergeMap, Observable, of, Subject, takeUntil } from 'rxjs';
import { SwiperComponent } from '../../../common/components';
import { InstaLearn, InstaLearnTopic } from '../../../common/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InstaLearnApiService } from '../../../common/services';

@Component({
    selector: 'lib-insta-cards-dialog',
    templateUrl: './insta-cards-dialog.component.html',
    styleUrls: ['./insta-cards-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InstaCardsDialogComponent implements OnInit, OnDestroy {
  public cardViewed: Subject<InstaLearn> = new Subject<InstaLearn>();

  @ViewChild(SwiperComponent) public set swiperComponent(value: SwiperComponent) {
    this._swiperComponent = value;

    this.swiperComponent?.swiper?.slideTo(this.data.activeIndex)
  }

  public get swiperComponent() {
    return this._swiperComponent;
  }

  public cards = signal([]);

  public topic: InstaLearnTopic;

  public muted = false;

  public swiperOptions = {
    slidesPerView: 'auto',
    centeredSlides: true,
    spaceBetween: 0,
    loop: false,
    breakpoints: {
      0: {
        slidesPerView: 1,
        spaceBetween: 16,
      },
      640: {
        slidesPerView: 'auto',
        spaceBetween: 0,
      }
    },
  };

  private unsubscribe: Subject<void> = new Subject();
  private _swiperComponent: SwiperComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { cards: InstaLearn[], activeIndex?: number, cardsSource: Observable<InstaLearn[]>, topic: InstaLearnTopic },
    private readonly dialogRef: MatDialogRef<InstaCardsDialogComponent>,
    private readonly destroyRef: DestroyRef,
    private readonly instaLearnApiService: InstaLearnApiService
  ) {}

  ngOnInit(): void {
    this.handleCardView();

    this.topic = this.data.topic;
    const source = this.data.cardsSource ?? of(this.data.cards ?? []);

    source
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe(data => this.cards.set(data));
  }

  public slideIndexChange(index: number): void {
    const card = this.cards()?.[index];
    this.cardViewed.next(card);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public closeDialog(item: InstaLearn) {
    this.dialogRef.close(item);
  }

  public mutedChanged(muted: boolean): void {
    this.muted = muted;
  }

  private handleCardView(): void {
    this.cardViewed.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(200),
      filter(Boolean),
      mergeMap((card) => this.instaLearnApiService.markInstaLearnAsViewed(card.id))
    ).subscribe();
  }
}
